import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    ArrowDown,
    ArrowRight,
    BarChart3,
    Building2,
    Cable,
    CheckCircle2,
    Clock3,
    CopyCheck,
    Database,
    Layers3,
    MailCheck,
    Package,
    Search,
    Send,
    ShieldCheck,
    XCircle,
    Zap,
} from "lucide-react";
import { ROUTES } from "../config/constants";
import PlanPreviewCard from "../components/home/PlanPreviewCard";
import {
    getPlanLeadsPerSearch,
    SUBSCRIPTION_TIERS,
} from "../utils/subscription";

const problemPoints = [
    "Researching companies and buyers takes time",
    "Writing relevant outreach at scale is difficult",
    "Following up across many leads becomes repetitive",
];

const solutionPoints = [
    "Search for leads in plain English",
    "Reuse existing researched data before running new external searches",
    "Create email-only outreach sequences from campaign context",
];

const workflowSteps = [
    {
        title: "Create Organization Context",
        description:
            "Define your company details, brand voice, target customers, and value proposition.",
        icon: Building2,
        cardClass:
            "border-indigo-100 bg-indigo-50 shadow-[0_18px_42px_rgba(79,70,229,0.12)]",
        iconClass: "text-indigo-600",
    },
    {
        title: "Add Product",
        description: "Define your product, positioning, and key value proposition.",
        icon: Package,
        cardClass:
            "border-violet-100 bg-violet-50 shadow-[0_18px_42px_rgba(124,58,237,0.12)]",
        iconClass: "text-violet-700",
    },
    {
        title: "Search Leads",
        description: "Search for target customers using plain-English lead queries.",
        icon: Search,
        cardClass:
            "border-cyan-100 bg-cyan-50 shadow-[0_18px_42px_rgba(8,145,178,0.12)]",
        iconClass: "text-cyan-700",
    },
    {
        title: "Create Connection",
        description: "Connect sender accounts using Google, SMTP, or supported channels.",
        icon: Cable,
        cardClass:
            "border-emerald-100 bg-emerald-50 shadow-[0_18px_42px_rgba(5,150,105,0.12)]",
        iconClass: "text-emerald-700",
    },
    {
        title: "Create Campaign",
        description: "Build multi-step email outreach sequences.",
        icon: Send,
        cardClass:
            "border-amber-100 bg-amber-50 shadow-[0_18px_42px_rgba(217,119,6,0.12)]",
        iconClass: "text-amber-700",
    },
    {
        title: "Track Progress",
        description: "Review campaign status, message counts, and progress.",
        icon: BarChart3,
        cardClass:
            "border-rose-100 bg-rose-50 shadow-[0_18px_42px_rgba(225,29,72,0.12)]",
        iconClass: "text-rose-700",
    },
];

const planPreviews = [
    {
        planName: "Starter",
        bestFor: "Best for individuals or small teams starting outreach.",
        highlights: [
            "500 leads per month",
            "2,500 email sends per month",
            `${getPlanLeadsPerSearch(SUBSCRIPTION_TIERS.STARTER)} leads per search`,
            "5 active campaigns",
            "Basic OptiMind AI scoring priority",
        ],
    },
    {
        planName: "Pro",
        bestFor: "Best for growing B2B sales teams running regular outreach.",
        highlights: [
            "750 leads per month",
            "5,000 email sends per month",
            `${getPlanLeadsPerSearch(SUBSCRIPTION_TIERS.PRO)} leads per search`,
            "15 active campaigns",
            "Medium OptiMind AI scoring priority",
        ],
        isPopular: true,
    },
    {
        planName: "Enterprise",
        bestFor:
            "Best for larger teams managing multi-product or multi-region outreach.",
        highlights: [
            "1,500 leads per month",
            "7,500 email sends per month",
            `${getPlanLeadsPerSearch(
                SUBSCRIPTION_TIERS.ENTERPRISE,
            )} leads per search`,
            "50 active campaigns",
            "High OptiMind AI scoring priority",
        ],
    },
];

const features = [
    {
        title: "Natural-language lead search",
        description: "Search using plain-English ICP queries instead of manual filters.",
        icon: Search,
        iconClass: "bg-indigo-50 text-indigo-600",
    },
    {
        title: "Smart dedup",
        description:
            "Reuse similar lead intelligence to avoid repeated searches, reduce API work, and save search credits.",
        icon: CopyCheck,
        iconClass: "bg-cyan-50 text-cyan-700",
    },
    {
        title: "Warehouse-first lookup",
        description:
            "Reuse existing researched leads before calling external providers.",
        icon: Database,
        iconClass: "bg-emerald-50 text-emerald-700",
    },
    {
        title: "Email outreach sequences",
        description:
            "Create email outreach campaigns with future support for social media outreach integrations.",
        icon: MailCheck,
        iconClass: "bg-violet-50 text-violet-700",
    },
];

const benefits = [
    {
        title: "Save research time",
        description:
            "Reuse existing lead intelligence before running new external searches.",
        icon: Clock3,
        iconClass: "bg-indigo-50 text-indigo-600",
    },
    {
        title: "Reduce repeated work",
        description:
            "Smart dedup helps teams avoid repeating the same search or API work.",
        icon: Zap,
        iconClass: "bg-cyan-50 text-cyan-700",
    },
    {
        title: "Run structured outreach",
        description:
            "Campaigns follow email-only multi-step sequences with clear context.",
        icon: Send,
        iconClass: "bg-emerald-50 text-emerald-700",
    },
    {
        title: "Control usage",
        description:
            "Credit envelopes and tier limits help avoid surprise overuse.",
        icon: ShieldCheck,
        iconClass: "bg-amber-50 text-amber-700",
    },
];

const SectionHeader = ({ title, subtitle }) => (
    <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            {title}
        </h2>
        {subtitle && (
            <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
                {subtitle}
            </p>
        )}
    </div>
);

const HomePage = () => {
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const ctaRoute = isAuthenticated ? ROUTES.DASHBOARD : ROUTES.SIGNUP;
    const ctaLabel = isAuthenticated ? "Go to Dashboard" : "Sign up to get started";

    return (
        <main className="min-h-screen overflow-x-hidden bg-white text-slate-950">
            <section
                className="px-4 py-16 text-white sm:px-6 sm:py-20 lg:px-8 lg:py-28"
                style={{
                    background:
                        "radial-gradient(circle at top right, rgba(99,102,241,0.32), transparent 34%), radial-gradient(circle at bottom left, rgba(79,70,229,0.2), transparent 28%), linear-gradient(135deg, #020617 0%, #11103a 52%, #050816 100%)",
                }}
            >
                <div className="mx-auto max-w-[1180px]">
                    <div className="mx-auto max-w-4xl text-center">
                        <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                            Autonomous outreach, without the{" "}
                            <span className="text-indigo-400">
                                manual grind.
                            </span>
                        </h1>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-slate-200 sm:text-lg">
                            OptiMind turns plain-English lead searches into
                            qualified leads, researched personalization, and
                            multi-step email outreach campaigns.
                        </p>
                        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                            <Link
                                to={ROUTES.MANAGE_SUBSCRIPTION}
                                className="inline-flex w-full max-w-xs items-center justify-center rounded-2xl border border-indigo-300/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-white/15 sm:w-auto sm:max-w-none"
                            >
                                Explore Plans
                            </Link>
                            <Link
                                to={ctaRoute}
                                className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-2xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-950/30 transition hover:bg-indigo-400 sm:w-auto sm:max-w-none"
                            >
                                {ctaLabel}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-white px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
                <div className="mx-auto grid max-w-[1180px] gap-8 lg:grid-cols-2 lg:gap-12">
                    <div className="lg:border-r lg:border-slate-200 lg:pr-12">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-500 ring-1 ring-rose-100">
                                <XCircle size={20} aria-hidden="true" />
                            </span>
                            <h2 className="text-2xl font-bold text-slate-950">
                                The problem
                            </h2>
                        </div>
                        <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                            The gap between knowing your ideal customer and
                            getting the first qualified meeting is still too
                            manual.
                        </p>
                        <ul className="mt-6 space-y-4">
                            {problemPoints.map((item) => (
                                <li
                                    key={item}
                                    className="flex gap-3 text-sm leading-6 text-slate-700"
                                >
                                    <XCircle
                                        size={17}
                                        className="mt-0.5 shrink-0 text-rose-500"
                                        aria-hidden="true"
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
                                <CheckCircle2 size={20} aria-hidden="true" />
                            </span>
                            <h2 className="text-2xl font-bold text-slate-950">
                                The solution
                            </h2>
                        </div>
                        <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                            OptiMind closes that gap with one workflow for lead
                            search, personalization, and outreach.
                        </p>
                        <ul className="mt-6 space-y-4">
                            {solutionPoints.map((item) => (
                                <li
                                    key={item}
                                    className="flex gap-3 text-sm leading-6 text-slate-700"
                                >
                                    <CheckCircle2
                                        size={17}
                                        className="mt-0.5 shrink-0 text-indigo-500"
                                        aria-hidden="true"
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-[#F8F8FE] px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
                <div className="mx-auto max-w-[1180px]">
                    <SectionHeader
                        title="The outreach workflow"
                        subtitle="A clear sequence from product context to tracked campaign execution."
                    />

                    <div className="relative mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:gap-6">
                        {workflowSteps.map((step, index) => {
                            const Icon = step.icon;
                            const isLastStep =
                                index === workflowSteps.length - 1;
                            const isDesktopRowEnd = (index + 1) % 3 === 0;
                            const shouldShowDesktopRightArrow =
                                !isLastStep && !isDesktopRowEnd;

                            return (
                                <div key={step.title} className="relative">
                                    <article
                                        className={`relative z-10 h-full rounded-3xl border p-5 text-center ${step.cardClass}`}
                                    >
                                        <span className="absolute left-1/2 top-0 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white shadow-sm">
                                            {index + 1}
                                        </span>
                                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-100">
                                            <Icon
                                                size={27}
                                                className={step.iconClass}
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <h3 className="mt-5 text-sm font-bold text-slate-950">
                                            {step.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-6 text-slate-600">
                                            {step.description}
                                        </p>
                                    </article>

                                    {!isLastStep && (
                                        <>
                                            {shouldShowDesktopRightArrow && (
                                                <ArrowRight
                                                    size={24}
                                                    className="pointer-events-none absolute -right-6 top-1/2 z-20 hidden -translate-y-1/2 text-indigo-300 lg:block"
                                                    aria-hidden="true"
                                                />
                                            )}
                                            <ArrowDown
                                                size={22}
                                                className="pointer-events-none absolute -bottom-7 left-1/2 z-20 -translate-x-1/2 text-indigo-300 sm:hidden"
                                                aria-hidden="true"
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-white px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
                <div className="mx-auto max-w-[1180px]">
                    <SectionHeader
                        title="Choose the right plan"
                        subtitle="Preview the plan highlights, then open the subscription page for full details."
                    />

                    <div className="mt-10 grid gap-5 lg:grid-cols-3">
                        {planPreviews.map((plan) => (
                            <PlanPreviewCard
                                key={plan.planName}
                                {...plan}
                                buttonLabel="Know More"
                                buttonTo={ROUTES.MANAGE_SUBSCRIPTION}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-white px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
                <div className="mx-auto max-w-[1180px]">
                    <SectionHeader title="What OptiMind offers" />

                    <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {features.map((feature) => {
                            const Icon = feature.icon;

                            return (
                                <article
                                    key={feature.title}
                                    className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/70 p-5 text-center shadow-sm transition hover:border-indigo-200 hover:shadow-md"
                                >
                                    <div
                                        className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${feature.iconClass}`}
                                    >
                                        <Icon size={24} aria-hidden="true" />
                                    </div>
                                    <h3 className="mt-4 text-sm font-bold leading-5 text-slate-950">
                                        {feature.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">
                                        {feature.description}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-[#F8F8FE] px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
                <div className="mx-auto max-w-[1180px]">
                    <SectionHeader title="Built for better outreach" />

                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {benefits.map((benefit) => {
                            const Icon = benefit.icon;

                            return (
                                <article
                                    key={benefit.title}
                                    className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-indigo-100 hover:shadow-md"
                                >
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${benefit.iconClass}`}
                                        >
                                            <Icon
                                                size={23}
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-bold leading-5 text-slate-950">
                                                {benefit.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-6 text-slate-600">
                                                {benefit.description}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-white px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
                <div className="mx-auto max-w-[960px] rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-6 text-center shadow-sm sm:p-10">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-100">
                        <Layers3 size={28} aria-hidden="true" />
                    </div>
                    <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                        Start building your outreach workflow
                    </h2>
                    <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                        Search your ICP, review leads, connect a sender, and
                        create your first email campaign.
                    </p>
                    <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link
                            to={ROUTES.MANAGE_SUBSCRIPTION}
                            className="inline-flex w-full max-w-xs items-center justify-center rounded-2xl border border-indigo-100 bg-white px-6 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 sm:w-auto sm:max-w-none"
                        >
                            Explore Plans
                        </Link>
                        <Link
                            to={ctaRoute}
                            className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 sm:w-auto sm:max-w-none"
                        >
                            {ctaLabel}
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HomePage;
